import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const KEY = "fa_cart_v1";

// Lightweight obfuscation so cart contents (just product IDs/qty/prices, no PII)
// aren't trivially modifiable in DevTools by casual users.
const encode = (data) => {
  try { return btoa(unescape(encodeURIComponent(JSON.stringify(data)))); }
  catch { return JSON.stringify(data); }
};
const decode = (raw) => {
  if (!raw) return [];
  try { return JSON.parse(decodeURIComponent(escape(atob(raw)))); }
  catch {
    try { return JSON.parse(raw); } catch { return []; }
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return decode(localStorage.getItem(KEY)); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(KEY, encode(items)); }
    catch (err) { console.warn("cart persist failed", err.message); }
  }, [items]);

  const add = useCallback((p, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === p.id);
      if (existing) {
        return prev.map((i) => (i.product_id === p.id ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [...prev, { product_id: p.id, name: p.name, price_cents: p.price_cents, quantity: qty, image_url: p.image_url }];
    });
    setOpen(true);
  }, []);

  const remove = useCallback((pid) => setItems((prev) => prev.filter((i) => i.product_id !== pid)), []);
  const setQty = useCallback((pid, q) => setItems((prev) => prev.map((i) => (i.product_id === pid ? { ...i, quantity: Math.max(1, q) } : i))), []);
  const clear = useCallback(() => setItems([]), []);

  const totalCents = useMemo(() => items.reduce((s, i) => s + i.price_cents * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, add, remove, setQty, clear, totalCents, count, open, setOpen }),
    [items, add, remove, setQty, clear, totalCents, count, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
